export const MAX_COPAY_DEDUCTIBLE_TO_DISPLAY_AMOUNT = 40;

export enum PatientIntakeQuestionType {
  a1c = "a1c",
  address = "address",
  alcoholFrequency = "alcoholFrequency",
  altoPharmacy = "altoPharmacy",
  bariatricSurgery = "bariatricSurgery",
  bloodPressure = "bloodPressure",
  choosePharmacy = "choosePharmacy",
  conditions = "conditions",
  confirmUser = "confirmUser",
  eatingDisorder = "eatingDisorder",
  familyHistory = "familyHistory",
  heartRate = "heartRate",
  heightWeight = "heightWeight",
  labs = "labs",
  labUploader = "labUploader",
  medication = "medication",
  medicationAllergies = "medicationAllergies",
  medicationAllergiesList = "medicationAllergiesList",
  medicationList = "medicationList",
  notAvailable = "notAvailable",
  originalConditions = "originalConditions",
  otherSurgeries = "otherSurgeries",
  otherSurgeriesList = "otherSurgeriesList",
  patientAcknowledgement = "patientAcknowledgement",
  patientAttestation = "patientAttestation",
  phoneNumber = "phoneNumber",
  pregnantOrBreastfeeding = "pregnantOrBreastfeeding",
  searchPharmacy = "searchPharmacy",
  selfAssessedHealth = "selfAssessedHealth",
  suicideOrSelfHarm = "suicideOrSelfHarm",
  useDrugs = "useDrugs",
  useTobacco = "useTobacco",
}

export const enum SchedulerFlow {
  INITIAL = "initial",
  FOLLOWUP = "followup",
  MAWL = "mawl",
}

export const enum ConsultType {
  EM = "EM",
  RPM = "RPM",
  MAWL = "MAWL",
  MAWLRPM = "MAWLRPM",
}

export function getConsultTypeForSchedulerFlow(
  flow: SchedulerFlow
): ConsultType {
  switch (flow) {
    case SchedulerFlow.MAWL:
      return ConsultType.MAWL;
    case SchedulerFlow.FOLLOWUP:
    case SchedulerFlow.INITIAL:
    default:
      return null;
  }
}

export function getSchedulerFlowForConsultType(
  consultType: ConsultType
): SchedulerFlow {
  switch (consultType) {
    case ConsultType.EM:
      return SchedulerFlow.INITIAL;
    case ConsultType.MAWL:
      return SchedulerFlow.MAWL;
    case ConsultType.RPM:
    case ConsultType.MAWLRPM:
      return SchedulerFlow.FOLLOWUP;
    default:
      return null;
  }
}

export enum EligibleStateCode {
  AZ = "AZ",
  CA = "CA",
  CT = "CT",
  FL = "FL",
  TX = "TX",
  VA = "VA",
}

export enum IneligibleStateCodeForNewPatients {
  FL = "FL",
}

// This was being used UserSegment/features.ts in combination with a BMI of >= 25 to determine eligibility for telehealth.
// That has been stopped for now, but we're keeping this here in case we need it again in the future.
export const ELIGIBLE_HEALTH_RISKS = [
  "heartDiseaseOrStroke",
  "highBloodPressure",
  "diabetes",
  "nash",
  "kidneyDisease",
  "highCholesterol",
  "asthma",
  "insomnia",
];

export type PayerInfo = {
  payerId: string;
  workflowId: string;
};

export enum Payer {
  aetna = "aetna",
  anthem = "anthem",
  bsca = "bsca",
  bcbs = "bcbs",
  cigna = "cigna",
  humana = "humana",
  kaiserPermanente = "kaiserPermanente",
  medicare = "medicare",
  tricare = "tricare",
  uhc = "uhc",
}

export enum SurveyType {
  // create a new consult
  Schedule = "Schedule",
  // move an existing consult to a new time
  Reschedule = "Reschedule",
}

export const EligiblePayerMappings: {
  [stateCode in EligibleStateCode]: {
    [payer in Payer]?: PayerInfo;
  };
} = {
  CA: {
    anthem: { payerId: "47198", workflowId: "weight-management-anthem" },
    bsca: { payerId: "BS001", workflowId: "weight-management-bsca" },
    uhc: { payerId: "87726", workflowId: "weight-management-uhc" },
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
  },
  TX: {
    uhc: { payerId: "87726", workflowId: "weight-management-uhc" },
    bcbs: {
      payerId: "84980",
      workflowId: "weight-management-bcbs-tx",
    },
  },
  FL: {
    bcbs: { payerId: "00590", workflowId: "weight-management-bcbs-fl" },
    aetna: { payerId: "60054", workflowId: "weight-management-aetna-fl" },
  },
  CT: {
    anthem: { payerId: "47198", workflowId: "weight-management-anthem" },
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
  },
  AZ: {
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
    uhc: { payerId: "87726", workflowId: "weight-management-uhc" },
  },
  VA: {
    aetna: { payerId: "60054", workflowId: "weight-management-aetna" },
  },
};

export const CONSENT_CHECKBOX_ID = "consents";
